import React, { useEffect, useState, useCallback } from 'react';
import {
  Box, Typography, Button, TextField, InputAdornment, Dialog, DialogTitle,
  DialogContent, DialogActions, Grid, Chip, CircularProgress, Select,
  MenuItem, FormControl, InputLabel, Table, TableBody, TableCell,
  TableHead, TableRow, Paper, Divider, IconButton, Autocomplete, Alert,
  Stepper, Step, StepLabel, Card, CardContent
} from '@mui/material';
import {
  Add, Search, CheckCircle, Delete, Visibility, LocalShipping,
  Inventory, Payment, ArrowForward
} from '@mui/icons-material';
import { useForm, useFieldArray, Controller } from 'react-hook-form';
import { suppliersAPI, productsAPI, inventoryAPI } from '../services/api';
import DataTable, { StatusChip } from '../components/common/DataTable';
import { toast } from 'react-toastify';
import { useSelector } from 'react-redux';

const fmt = (n) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(n || 0);

const PO_STEPS = ['Draft', 'Pending Approval', 'Approved', 'Received', 'Paid'];
const PO_STEP_MAP = {
  draft: 0, pending_approval: 1, approved: 2,
  ordered: 2, partially_received: 3, received: 3, paid: 4, cancelled: -1,
};

// ── Create PO Dialog ────────────────────────────────────────────────────────
const CreatePODialog = ({ open, onClose, onSaved }) => {
  const [suppliers, setSuppliers] = useState([]);
  const [products, setProducts] = useState([]);
  const [warehouses, setWarehouses] = useState([]);
  const [loading, setLoading] = useState(false);
  const { register, handleSubmit, control, watch, setValue } = useForm({
    defaultValues: { currency: 'USD', warehouse_id: '', items: [{ product_id: '', quantity_ordered: 1, unit_cost: 0, discount_percent: 0 }] },
  });
  const { fields, append, remove } = useFieldArray({ control, name: 'items' });
  const items = watch('items') || [];

  useEffect(() => {
    if (!open) return;
    suppliersAPI.getAll({ limit: 500 }).then(r => setSuppliers(r.data.data || [])).catch(() => {});
    productsAPI.getAll({ limit: 500 }).then(r => setProducts(r.data.data || [])).catch(() => {});
    inventoryAPI.getWarehouses().then(r => setWarehouses(r.data.data || [])).catch(() => {});
  }, [open]);

  const subtotal = items.reduce((sum, item) => {
    const gross = (parseFloat(item.unit_cost) || 0) * (parseInt(item.quantity_ordered) || 0);
    return sum + gross * (1 - (parseFloat(item.discount_percent) || 0) / 100);
  }, 0);

  const onSubmit = async (data) => {
    try {
      setLoading(true);
      await suppliersAPI.createPurchaseOrder({
        ...data,
        items: data.items.map(i => ({
          product_id: i.product_id,
          quantity_ordered: parseInt(i.quantity_ordered) || 1,
          unit_cost: parseFloat(i.unit_cost) || 0,
          discount_percent: parseFloat(i.discount_percent) || 0,
        })),
      });
      toast.success('Purchase order created');
      onSaved(); onClose();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to create');
    } finally { setLoading(false); }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <form onSubmit={handleSubmit(onSubmit)}>
        <DialogTitle fontWeight={700}>Create Purchase Order</DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid item xs={12} md={4}>
              <Controller name="supplier_id" control={control} rules={{ required: 'Supplier required' }}
                render={({ field, fieldState }) => (
                  <Autocomplete options={suppliers} getOptionLabel={o => `${o.name} (${o.supplier_code})`}
                    onChange={(_, v) => field.onChange(v?.id || '')}
                    renderInput={p => <TextField {...p} label="Supplier *" size="small"
                      error={!!fieldState.error} helperText={fieldState.error?.message} />} />
                )} />
            </Grid>
            <Grid item xs={12} md={4}>
              <Controller name="warehouse_id" control={control} rules={{ required: 'Warehouse required' }}
                render={({ field, fieldState }) => (
                  <FormControl fullWidth size="small" error={!!fieldState.error}>
                    <InputLabel>Receive to Warehouse *</InputLabel>
                    <Select {...field} value={field.value || ''} label="Receive to Warehouse *">
                      <MenuItem value="">— Select —</MenuItem>
                      {warehouses.map(w => <MenuItem key={w.id} value={w.id}>{w.name}</MenuItem>)}
                    </Select>
                    {fieldState.error && <Typography variant="caption" color="error" sx={{ ml: 1.5 }}>{fieldState.error.message}</Typography>}
                  </FormControl>
                )} />
            </Grid>
            <Grid item xs={6} md={2}>
              <TextField fullWidth size="small" label="Expected Date" type="date"
                InputLabelProps={{ shrink: true }} {...register('expected_date')} />
            </Grid>
            <Grid item xs={6} md={2}>
              <TextField fullWidth size="small" label="Currency" {...register('currency')} />
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth size="small" label="Notes" {...register('notes')} />
            </Grid>
          </Grid>

          <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1 }}>Order Items</Typography>
          <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Product / Vehicle</TableCell>
                  <TableCell width={90}>Qty</TableCell>
                  <TableCell width={130}>Unit Cost</TableCell>
                  <TableCell width={100}>Discount %</TableCell>
                  <TableCell width={120}>Line Total</TableCell>
                  <TableCell width={40}></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {fields.map((field, idx) => {
                  const gross = (parseFloat(items[idx]?.unit_cost) || 0) * (parseInt(items[idx]?.quantity_ordered) || 0);
                  const lineTotal = gross * (1 - (parseFloat(items[idx]?.discount_percent) || 0) / 100);
                  return (
                    <TableRow key={field.id}>
                      <TableCell>
                        <Controller name={`items.${idx}.product_id`} control={control} rules={{ required: true }}
                          render={({ field: f }) => (
                            <Autocomplete size="small" options={products}
                              getOptionLabel={o => `${o.brand_name || ''} ${o.name} (${o.sku})`}
                              onChange={(_, v) => { f.onChange(v ? v.id : ''); if (v) setValue(`items.${idx}.unit_cost`, parseFloat(v.cost_price) || 0); }}
                              renderInput={p => <TextField {...p} size="small" placeholder="Search product..." sx={{ minWidth: 260 }} />} />
                          )} />
                      </TableCell>
                      <TableCell>
                        <TextField size="small" type="number" sx={{ width: 75 }}
                          {...register(`items.${idx}.quantity_ordered`, { min: 1, valueAsNumber: true })} defaultValue={1} inputProps={{ min: 1 }} />
                      </TableCell>
                      <TableCell>
                        <TextField size="small" type="number" sx={{ width: 120 }}
                          {...register(`items.${idx}.unit_cost`, { valueAsNumber: true })}
                          InputProps={{ startAdornment: <span style={{ marginRight: 4 }}>$</span> }} />
                      </TableCell>
                      <TableCell>
                        <TextField size="small" type="number" sx={{ width: 80 }}
                          {...register(`items.${idx}.discount_percent`, { min: 0, max: 100, valueAsNumber: true })} defaultValue={0} />
                      </TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>{fmt(lineTotal)}</TableCell>
                      <TableCell>
                        {fields.length > 1 && <IconButton size="small" color="error" onClick={() => remove(idx)}><Delete fontSize="small" /></IconButton>}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
            <Button size="small" startIcon={<Add />} sx={{ mt: 1 }}
              onClick={() => append({ product_id: '', quantity_ordered: 1, unit_cost: 0, discount_percent: 0 })}>
              Add Item
            </Button>
          </Paper>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Box sx={{ minWidth: 220, bgcolor: '#f5f6fa', borderRadius: 2, p: 2 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography fontWeight={700}>Total:</Typography>
                <Typography fontWeight={700} color="primary">{fmt(subtotal)}</Typography>
              </Box>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={onClose}>Cancel</Button>
          <Button type="submit" variant="contained" startIcon={<LocalShipping />} disabled={loading}>
            {loading ? <CircularProgress size={20} /> : 'Create Purchase Order'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

// ── Receive Goods Dialog ─────────────────────────────────────────────────────
const ReceiveGoodsDialog = ({ open, onClose, po, onSaved }) => {
  const [loading, setLoading] = useState(false);
  const [poItems, setPOItems] = useState([]);
  const [received, setReceived] = useState({});
  const [notes, setNotes] = useState('');

  useEffect(() => {
    if (open && po) {
      suppliersAPI.getPOItems(po.id).then(r => {
        const items = r.data.data || [];
        setPOItems(items);
        // Default received qty = ordered - already received
        const defaults = {};
        items.forEach(item => {
          defaults[item.id] = Math.max(0, (item.quantity_ordered || 0) - (item.quantity_received || 0));
        });
        setReceived(defaults);
      }).catch(() => toast.error('Failed to load PO items'));
    }
  }, [open, po]);

  const handleSubmit = async () => {
    try {
      setLoading(true);
      const items = poItems.map(item => ({
        po_item_id: item.id,
        product_id: item.product_id,
        quantity_received: parseInt(received[item.id]) || 0,
        quantity_accepted: parseInt(received[item.id]) || 0,
        unit_cost: item.unit_cost,
      })).filter(i => i.quantity_received > 0);

      if (!items.length) { toast.error('Enter at least one received quantity'); setLoading(false); return; }

      await suppliersAPI.receivePurchaseOrder(po.id, { items, notes });
      toast.success('Goods received! Stock has been updated.');
      onSaved(); onClose();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to receive goods');
    } finally { setLoading(false); }
  };

  if (!po) return null;
  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle fontWeight={700}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Inventory color="primary" />
          Receive Goods — {po.po_number}
        </Box>
      </DialogTitle>
      <DialogContent dividers>
        <Alert severity="info" sx={{ mb: 2 }}>
          Enter the quantity received for each item. Accepted quantity will be added to <strong>{po.warehouse_name || 'the warehouse'}</strong>.
        </Alert>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Product</TableCell>
              <TableCell align="right">Ordered</TableCell>
              <TableCell align="right">Already Received</TableCell>
              <TableCell align="right">Receiving Now *</TableCell>
              <TableCell align="right">Remaining</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {poItems.map(item => {
              const pending = Math.max(0, (item.quantity_ordered || 0) - (item.quantity_received || 0));
              return (
                <TableRow key={item.id}>
                  <TableCell>
                    <Typography variant="body2" fontWeight={600}>{item.product_name}</Typography>
                    <Typography variant="caption" color="text.secondary">{item.sku}</Typography>
                  </TableCell>
                  <TableCell align="right">{item.quantity_ordered}</TableCell>
                  <TableCell align="right">{item.quantity_received || 0}</TableCell>
                  <TableCell align="right" sx={{ width: 120 }}>
                    <TextField size="small" type="number" sx={{ width: 90 }}
                      value={received[item.id] ?? 0}
                      onChange={e => setReceived(prev => ({ ...prev, [item.id]: e.target.value }))}
                      inputProps={{ min: 0, max: pending }} />
                  </TableCell>
                  <TableCell align="right">
                    <Chip label={pending - (parseInt(received[item.id]) || 0)}
                      color={pending - (parseInt(received[item.id]) || 0) <= 0 ? 'success' : 'warning'}
                      size="small" />
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
        <TextField fullWidth label="Notes" multiline rows={2} sx={{ mt: 2 }}
          value={notes} onChange={e => setNotes(e.target.value)} />
      </DialogContent>
      <DialogActions sx={{ p: 2 }}>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" color="success" startIcon={<Inventory />}
          onClick={handleSubmit} disabled={loading}>
          {loading ? <CircularProgress size={20} /> : 'Confirm Receipt & Update Stock'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// ── Record Payment Dialog ────────────────────────────────────────────────────
const RecordPaymentDialog = ({ open, onClose, po, onSaved }) => {
  const { register, handleSubmit, reset } = useForm();
  const [loading, setLoading] = useState(false);

  useEffect(() => { if (open) reset({ amount: po?.total_amount, payment_method: 'bank_transfer' }); }, [open, po, reset]);

  const onSubmit = async (data) => {
    try {
      setLoading(true);
      await suppliersAPI.recordPOPayment(po.id, data);
      toast.success('Payment recorded');
      onSaved(); onClose();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to record payment');
    } finally { setLoading(false); }
  };

  if (!po) return null;
  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <form onSubmit={handleSubmit(onSubmit)}>
        <DialogTitle fontWeight={700}>Record Payment — {po.po_number}</DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField fullWidth label="Amount *" type="number" size="small"
                {...register('amount', { required: true, valueAsNumber: true })}
                InputProps={{ startAdornment: <span style={{ marginRight: 4 }}>$</span> }} />
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth size="small">
                <InputLabel>Payment Method</InputLabel>
                <Select {...register('payment_method')} label="Payment Method" defaultValue="bank_transfer">
                  <MenuItem value="cash">Cash</MenuItem>
                  <MenuItem value="bank_transfer">Bank Transfer</MenuItem>
                  <MenuItem value="cheque">Cheque</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth label="Reference Number" size="small" {...register('reference_number')} />
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth label="Payment Date" type="date" size="small"
                InputLabelProps={{ shrink: true }} {...register('payment_date')} />
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth label="Notes" size="small" {...register('notes')} />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={onClose}>Cancel</Button>
          <Button type="submit" variant="contained" color="success" startIcon={<Payment />} disabled={loading}>
            {loading ? <CircularProgress size={20} /> : 'Record Payment'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

// ── PO Detail Dialog ─────────────────────────────────────────────────────────
const PODetailDialog = ({ open, onClose, po, onSaved }) => {
  const { user } = useSelector(s => s.auth);
  const canApprove = user?.roles?.some(r => ['admin', 'manager'].includes(r));
  const canReceive = user?.roles?.some(r => ['admin', 'manager', 'storekeeper'].includes(r));
  const canPay = user?.roles?.some(r => ['admin', 'manager', 'accountant'].includes(r));

  const [approving, setApproving] = useState(false);
  const [receiveOpen, setReceiveOpen] = useState(false);
  const [paymentOpen, setPaymentOpen] = useState(false);

  if (!po) return null;

  const currentStep = PO_STEP_MAP[po.status] ?? 0;

  const handleApprove = async () => {
    try {
      setApproving(true);
      await suppliersAPI.approvePurchaseOrder(po.id);
      toast.success('Purchase order approved');
      onSaved(); onClose();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to approve');
    } finally { setApproving(false); }
  };

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography fontWeight={700} variant="h6">PO: {po.po_number}</Typography>
            <StatusChip status={po.status} />
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          {/* Progress Stepper */}
          {po.status !== 'cancelled' && (
            <Stepper activeStep={currentStep} sx={{ mb: 3 }}>
              {PO_STEPS.map(label => (
                <Step key={label}><StepLabel>{label}</StepLabel>
              </Step>))}
            </Stepper>
          )}

          {/* Info grid */}
          <Grid container spacing={2} sx={{ mb: 2 }}>
            {[
              { label: 'Supplier', value: po.supplier_name },
              { label: 'Warehouse', value: po.warehouse_name || '—' },
              { label: 'Order Date', value: po.order_date ? new Date(po.order_date).toLocaleDateString() : '—' },
              { label: 'Expected Date', value: po.expected_date ? new Date(po.expected_date).toLocaleDateString() : '—' },
              { label: 'Total Amount', value: fmt(po.total_amount) },
              { label: 'Payment Status', value: po.payment_status || 'unpaid' },
            ].map(item => (
              <Grid item xs={6} md={4} key={item.label}>
                <Typography variant="caption" color="text.secondary">{item.label}</Typography>
                <Typography variant="body2" fontWeight={600}>{item.value}</Typography>
              </Grid>
            ))}
          </Grid>

          {po.notes && (
            <Box sx={{ bgcolor: '#f5f6fa', borderRadius: 1, p: 1.5, mb: 2 }}>
              <Typography variant="body2"><strong>Notes:</strong> {po.notes}</Typography>
            </Box>
          )}

          {/* Action buttons */}
          <Divider sx={{ my: 2 }} />
          <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>Actions</Typography>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            {canApprove && po.status === 'pending_approval' && (
              <Button variant="contained" color="primary" startIcon={<CheckCircle />}
                onClick={handleApprove} disabled={approving}>
                {approving ? <CircularProgress size={18} /> : 'Approve Order'}
              </Button>
            )}
            {canReceive && ['approved', 'ordered', 'partially_received'].includes(po.status) && (
              <Button variant="contained" color="success" startIcon={<Inventory />}
                onClick={() => setReceiveOpen(true)}>
                Receive Goods
              </Button>
            )}
            {canPay && ['approved', 'ordered', 'partially_received', 'received'].includes(po.status)
              && po.payment_status !== 'paid' && (
              <Button variant="outlined" color="success" startIcon={<Payment />}
                onClick={() => setPaymentOpen(true)}>
                Record Payment
              </Button>
            )}
            {po.status === 'received' && po.payment_status === 'paid' && (
              <Chip label="✓ Fully Complete" color="success" />
            )}
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={onClose}>Close</Button>
        </DialogActions>
      </Dialog>

      <ReceiveGoodsDialog open={receiveOpen} onClose={() => setReceiveOpen(false)}
        po={po} onSaved={() => { onSaved(); onClose(); }} />
      <RecordPaymentDialog open={paymentOpen} onClose={() => setPaymentOpen(false)}
        po={po} onSaved={() => { onSaved(); onClose(); }} />
    </>
  );
};

// ── Main Page ─────────────────────────────────────────────────────────────────
const PurchaseOrdersPage = () => {
  const { user } = useSelector(s => s.auth);
  const canCreate = user?.roles?.some(r => ['admin', 'manager', 'storekeeper'].includes(r));

  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [statusFilter, setStatusFilter] = useState('');
  const [createOpen, setCreateOpen] = useState(false);
  const [selectedPO, setSelectedPO] = useState(null);

  const fetchOrders = useCallback(async () => {
    try {
      setLoading(true);
      const { data } = await suppliersAPI.getPurchaseOrders({
        page: page + 1, limit: rowsPerPage,
        status: statusFilter || undefined,
      });
      setOrders(data.data || []);
      setTotal(data.pagination?.total || 0);
    } catch { toast.error('Failed to load purchase orders'); }
    finally { setLoading(false); }
  }, [page, rowsPerPage, statusFilter]);

  useEffect(() => { fetchOrders(); }, [fetchOrders]);

  const columns = [
    { key: 'po_number', label: 'PO Number', render: v => <Typography fontFamily="monospace" fontWeight={700} fontSize="0.85rem">{v}</Typography> },
    { key: 'supplier_name', label: 'Supplier', render: v => <Typography fontWeight={600}>{v}</Typography> },
    { key: 'warehouse_name', label: 'Warehouse', render: v => v || '—' },
    { key: 'order_date', label: 'Date', render: v => new Date(v).toLocaleDateString() },
    { key: 'total_amount', label: 'Total', align: 'right', render: v => <Typography fontWeight={700}>{fmt(v)}</Typography> },
    { key: 'status', label: 'Status', render: v => <StatusChip status={v} /> },
    {
      key: 'payment_status', label: 'Payment',
      render: v => <Chip label={(v || 'unpaid').replace(/_/g, ' ').toUpperCase()}
        color={v === 'paid' ? 'success' : v === 'partially_paid' ? 'warning' : 'error'}
        size="small" sx={{ fontWeight: 700, fontSize: '0.7rem' }} />
    },
    { key: 'id', label: '', render: (_, row) => (
      <Button size="small" startIcon={<Visibility />} variant="outlined"
        onClick={() => setSelectedPO(row)}>View</Button>
    )},
  ];

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h4" fontWeight={700}>Purchase Orders</Typography>
          <Typography variant="body2" color="text.secondary">{total} total orders</Typography>
        </Box>
        {canCreate && (
          <Button variant="contained" startIcon={<Add />} onClick={() => setCreateOpen(true)}>
            New Purchase Order
          </Button>
        )}
      </Box>

      <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
        <FormControl size="small" sx={{ minWidth: 200 }}>
          <InputLabel>Filter by Status</InputLabel>
          <Select label="Filter by Status" value={statusFilter}
            onChange={e => { setStatusFilter(e.target.value); setPage(0); }}>
            <MenuItem value="">All Statuses</MenuItem>
            {['draft','pending_approval','approved','ordered','partially_received','received','cancelled'].map(s => (
              <MenuItem key={s} value={s}>{s.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}</MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      <DataTable columns={columns} rows={orders} loading={loading} total={total}
        page={page} rowsPerPage={rowsPerPage}
        onPageChange={(_, p) => setPage(p)}
        onRowsPerPageChange={e => { setRowsPerPage(parseInt(e.target.value)); setPage(0); }}
        emptyMessage="No purchase orders found" />

      <CreatePODialog open={createOpen} onClose={() => setCreateOpen(false)} onSaved={fetchOrders} />
      <PODetailDialog open={!!selectedPO} onClose={() => setSelectedPO(null)}
        po={selectedPO} onSaved={fetchOrders} />
    </Box>
  );
};

export default PurchaseOrdersPage;
